import type { Payment } from "@shared/types/payment.types"

export interface TransformedPaymentData {
  amount: number
  currency: string
  decimals?: number
  status: string
  customer: string | null
  description: string | null
  paymentMethod: string
  isCardPayment: boolean
  createdAt: string | number | Date | null
  channel: string | null
  channelId: string | null
  declineReason: string | null
  riskScore: string | null
  merchant: string | null
  program: string | null
  organization: string | null
  bank: string | null
  productPlatform: string | null
  processor: string | null
  refundReason: string | null
  requestedAt: string | number | Date | null
  refundedAt: string | number | Date | null
  refundedBy: string | null
  eligibility: string | null
  disputeStage: string | null
  disputeReason: string | null
  disputedOn: string | number | Date | null
  winLikelihood: string | null
  evidenceDueBy: string | number | Date | null
  evidenceSubmittedBy: string | null
  failureReason: string | null
  failedAt: string | number | Date | null
  captureBy: string | number | Date | null
  store: string | null
  lastUpdated: string | number | Date | null
}

const _cache = new WeakMap<Payment, TransformedPaymentData>()

export function transformPaymentData(payment: Payment): TransformedPaymentData {
  const cached = _cache.get(payment)
  if (cached) return cached

  const result = _transformPaymentData(payment)
  _cache.set(payment, result)
  return result
}

function _transformPaymentData(payment: Payment): TransformedPaymentData {
  const amount = payment.amounts?.original?.amount ?? payment.amount?.amount ?? 0
  const decimals = payment.amounts?.original?.decimal ?? payment.amount?.decimal
  const currency = payment.amounts?.original?.currency ?? payment.amount?.currency ?? payment.currency ?? 'USD'
  
  const paymentMethodRaw = payment.paymentMethod
  const paymentMethod = typeof paymentMethodRaw === 'string' 
    ? paymentMethodRaw 
    : paymentMethodRaw?.displayString || "Unknown"
  
  const customer = payment.customer?.name ||
                   payment.customer?.email ||
                   payment.customer?.id ||
                   payment.account?.name ||
                   null
  
  const description = payment.description || 
                      payment.transactionRef || 
                      payment.id?.toString() || 
                      null

  return {
    amount,
    currency,
    decimals,
    status: payment.status ?? "unknown",
    customer,
    description,
    paymentMethod,
    isCardPayment: paymentMethod.includes('•••'),
    createdAt: payment.createdAt || payment.timestamps?.initiated || null,
    channel: payment.channel
      ? `${payment.channel.charAt(0).toUpperCase()}${payment.channel.slice(1)}`
      : null,
    channelId: payment.channelId || null,
    declineReason: payment.declineReason || null,
    riskScore: payment.riskScore?.toString() || null,
    merchant: payment.merchant || null,
    program: payment.program || null,
    organization: payment.organization || null,
    bank: payment.bank || null,
    productPlatform: payment.productPlatform || null,
    processor: payment.processor || null,
    refundReason: null, // Will be populated from refund data when available
    requestedAt: payment.timestamps?.initiated || null,
    refundedAt: payment.createdAt || null, // Will be populated from refund data when available
    refundedBy: null, // Will be populated from refund data when available
    eligibility: payment.eligibility || null,
    disputeStage: null, // Will be populated from dispute data when available
    disputeReason: null, // Will be populated from dispute data when available
    disputedOn: null, // Will be populated from dispute data when available
    winLikelihood: null, // Will be populated from dispute data when available
    evidenceDueBy: null, // Will be populated from dispute data when available
    evidenceSubmittedBy: null, // Will be populated from dispute data when available
    failureReason: payment.declineReason || null,
    failedAt: payment.timestamps?.authorized || null,
    captureBy: payment.timestamps?.authorized || null,
    store: payment.store || null,
    lastUpdated: payment.createdAt || null, // Will be populated when lastUpdated timestamp is available
  }
}
